import React, { useMemo, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import "./index.css";
import AugmentHR from "./App";
import Login from "./Login";
import SetPassword from "./SetPassword";
import PublicOffer from "./PublicOffer";
import PublicOnboarding from "./PublicOnboarding";
import PublicAssessment from "./PublicAssessment";
import { AuthProvider, useAuth } from "./auth";
import { SettingsProvider, useAppSettings } from "./SettingsContext";
import SettingsDrawer from "./SettingsDrawer";
import { createAugmentTheme } from "./theme";

function Root() {
  const { user, loading } = useAuth();
  // Public, unauthenticated pages reached from emailed links (invite / reset).
  const path = window.location.pathname;
  const isPublicPwPage = path === "/set-password" || path === "/reset";
  const isPublicOffer = path.startsWith("/offer/");
  const isPublicOnboarding = path.startsWith("/onboarding/");
  const isPublicAssessment = path.startsWith("/assessment/");
  // Routing is JWT-gated: without a valid session the only reachable screen is
  // the login page, and the URL reflects that. After login, AugmentHR takes the
  // URL over and routes to the requested (or default) tab.
  useEffect(() => {
    if (loading) return;
    if (!user && !isPublicPwPage && !isPublicOffer && !isPublicOnboarding && !isPublicAssessment && path !== "/login") {
      window.history.replaceState(null, "", "/login");
    }
  }, [loading, user, isPublicPwPage, isPublicOffer, isPublicOnboarding, isPublicAssessment, path]);
  // Public pages need no session — render them directly.
  if (isPublicOffer) return <PublicOffer />;
  if (isPublicOnboarding) return <PublicOnboarding />;
  if (isPublicAssessment) return <PublicAssessment />;
  if (isPublicPwPage) return <SetPassword />;
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
          backgroundColor: "background.default",
        }}
      >
        <CircularProgress size={48} />
        <span style={{ color: "#6C737F", fontFamily: "Inter, system-ui, sans-serif" }}>
          Loading…
        </span>
      </Box>
    );
  }
  return user ? <AugmentHR /> : <Login />;
}

function ThemedApp() {
  const { settings } = useAppSettings();
  const theme = useMemo(() => createAugmentTheme(settings), [settings]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Root />
        <SettingsDrawer />
      </AuthProvider>
    </ThemeProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <SettingsProvider>
      <ThemedApp />
    </SettingsProvider>
  </React.StrictMode>
);
